import { type PropsWithChildren, type JSX, useContext } from 'react';
import { useIntl } from 'react-intl';

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CopyIcon,
  PinIcon,
  PinOffIcon,
  ViewOffIcon,
  WrapTextIcon,
} from '@dynatrace/strato-icons';

import { useCopyToClipboard } from '../../../../core/utils/useCopyToClipboard.js';
import { Group as MenuGroup } from '../../../../navigation/menu/Group.js';
import { Intent as MenuIntent } from '../../../../navigation/menu/Intent.js';
import { Item as MenuItem } from '../../../../navigation/menu/Item.js';
import { Label as MenuLabel } from '../../../../navigation/menu/Label.js';
import { Link as MenuLink } from '../../../../navigation/menu/Link.js';
import { Prefix as MenuPrefix } from '../../../../navigation/menu/Prefix.js';
import { Sub as MenuSub } from '../../../../navigation/menu/Sub.js';
import { SubContent as MenuSubContent } from '../../../../navigation/menu/SubContent.js';
import { SubTrigger as MenuSubTrigger } from '../../../../navigation/menu/SubTrigger.js';
import { Suffix as MenuSuffix } from '../../../../navigation/menu/Suffix.js';
import {
  useDataTable,
  useDataTableColumn,
  useDataTableHeader,
  DataTableHeaderContext,
} from '../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../messages.js';

/**
 * Defines the props for built-in table action menu items.
 * @public
 */
export type DataTableActionsMenuActionProps = {
  /**
   * Optional callback that is invoked when the action is executed.
   */
  onSelect?: () => void;
};

/**
 * Defines the props on the `CopyItem` table actions menu for copying the cell content to clipboard.
 * @public
 */
export type DataTableActionsMenuCopyItemProps =
  DataTableActionsMenuActionProps & {
    /**
     * The string representation of the cell value that gets copied to the clipboard when the user selects the action item.
     */
    value: string;
  };

/**
 * Out of the box cell action for copying the cell content to clipboard.
 * This component can only be used as a child element of `<TableActionsMenu>`.
 */
const CopyItem = ({ value, onSelect }: DataTableActionsMenuCopyItemProps) => {
  const intl = useIntl();
  const [copyToClipboard] = useCopyToClipboard(value);
  const header = useContext(DataTableHeaderContext);
  const isColumnAction = header !== null;
  const dataDtName = isColumnAction
    ? 'ColumnActionCopyItem'
    : 'CellActionCopyItem';

  return (
    <DataTableActionsMenu.Item
      onSelect={() => {
        copyToClipboard();
        onSelect?.();
      }}
      data-dt-name={dataDtName}
    >
      <DataTableActionsMenu.Prefix>
        <CopyIcon />
      </DataTableActionsMenu.Prefix>
      {intl.formatMessage(dataTableMessages.copyValueCellAction)}
    </DataTableActionsMenu.Item>
  );
};

/**
 * Out of the box column actions for toggling line wrap.
 * This component can only be used as a child element of `<TableActionsMenu>`.
 */
const LineWrapAction = ({
  onSelect,
}: DataTableActionsMenuActionProps): JSX.Element => {
  const intl = useIntl();
  const column = useDataTableColumn();
  const isLineWrapped = column?.getIsLineWrapped();
  const lineWrapDtProperties = `state:${isLineWrapped ? 'unwrap' : 'wrap'}`;

  return (
    <DataTableActionsMenu.Item
      onSelect={() => {
        column.toggleLineWrap();
        onSelect?.();
      }}
      data-dt-name="ColumnActionLineWrap"
      data-dt-properties={lineWrapDtProperties}
    >
      <DataTableActionsMenu.Prefix>
        <WrapTextIcon />
      </DataTableActionsMenu.Prefix>
      {isLineWrapped
        ? intl.formatMessage(dataTableMessages.disableLineWrapLabel)
        : intl.formatMessage(dataTableMessages.enableLineWrapLabel)}
    </DataTableActionsMenu.Item>
  );
};

/**
 * Out of the box column action for hiding a column.
 * This component can only be used as a child element of `<TableActionsMenu>`.
 * @internal
 */
export const HideColumnAction = ({
  onSelect,
}: DataTableActionsMenuActionProps) => {
  const intl = useIntl();
  const table = useDataTable();
  const currentVisibility = table.atoms.columnVisibility?.get();
  const column = useDataTableColumn();
  const childColumns = column.columns;

  const handleSelect = (): void => {
    if (childColumns.length === 0) {
      column.toggleVisibility();
    } else {
      const updatedChildrenVisibility = childColumns.reduce(
        (updatedVisibility: Record<string, boolean>, childColumn) => {
          return {
            ...updatedVisibility,
            [childColumn.id]: false,
          };
        },
        {},
      );
      table.setColumnVisibility({
        ...currentVisibility,
        ...updatedChildrenVisibility,
      });
    }
  };

  return (
    <DataTableActionsMenu.Item
      onSelect={() => {
        handleSelect();
        onSelect?.();
      }}
      disabled={!column.getCanHide()}
      data-dt-name="ColumnActionHide"
    >
      <DataTableActionsMenu.Prefix>
        <ViewOffIcon />
      </DataTableActionsMenu.Prefix>
      {intl.formatMessage(dataTableMessages.hideColumnLabel)}
    </DataTableActionsMenu.Item>
  );
};

/**
 * Out of the box column action for reordering columns.
 * This component can only be used as a child element of `<TableActionsMenu>`.
 * @internal
 */
export const ColumnOrderAction = ({
  onSelect,
}: DataTableActionsMenuActionProps) => {
  const intl = useIntl();
  const column = useDataTableColumn();
  const header = useDataTableHeader();
  const isPinnedColumn = !!column.getIsPinned();
  const table = useDataTable();

  if (!table.optionsStore.state.enableColumnOrdering) {
    return null;
  }

  const orderActions = () => (
    <>
      <DataTableActionsMenu.Item
        onSelect={() => {
          column.moveColumnLeft();
          onSelect?.();
        }}
        disabled={!column.getCanMoveColumnLeft()}
        data-dt-name="ColumnActionMoveLeft"
      >
        <DataTableActionsMenu.Prefix>
          <ArrowLeftIcon />
        </DataTableActionsMenu.Prefix>
        {intl.formatMessage(dataTableMessages.moveColumnLeftLabel)}
      </DataTableActionsMenu.Item>
      <DataTableActionsMenu.Item
        onSelect={() => {
          column.moveColumnRight();
          onSelect?.();
        }}
        disabled={!column.getCanMoveColumnRight()}
        data-dt-name="ColumnActionMoveRight"
      >
        <DataTableActionsMenu.Prefix>
          <ArrowRightIcon />
        </DataTableActionsMenu.Prefix>
        {intl.formatMessage(dataTableMessages.moveColumnRightLabel)}
      </DataTableActionsMenu.Item>
    </>
  );

  // if a parent has pinned children, show order actions based on the location of the header
  if (isPinnedColumn) {
    if (header.id.includes('center')) {
      return orderActions();
    }

    return null;
  }

  return orderActions();
};

/**
 * Out of the box column action for pinning a column.
 * This component can only be used as a child element of `<TableActionsMenu>`.
 */
const ColumnPinningAction = ({
  onSelect,
}: DataTableActionsMenuActionProps): JSX.Element | null => {
  const intl = useIntl();
  const table = useDataTable();
  const header = useDataTableHeader();
  if (!table.options.enableColumnPinning) {
    return null;
  }

  const pinnedState = header.getIsPinned();
  return (
    <>
      {pinnedState !== 'left' ? (
        <DataTableActionsMenu.Item
          onSelect={() => {
            header.pin('left');
            onSelect?.();
          }}
          data-dt-name="ColumnActionPinLeft"
        >
          <DataTableActionsMenu.Prefix>
            <PinIcon />
          </DataTableActionsMenu.Prefix>
          {intl.formatMessage(dataTableMessages.pinColumnLeftLabel)}
        </DataTableActionsMenu.Item>
      ) : null}
      {pinnedState !== 'right' ? (
        <DataTableActionsMenu.Item
          onSelect={() => {
            header.pin('right');
            onSelect?.();
          }}
          data-dt-name="ColumnActionPinRight"
        >
          <DataTableActionsMenu.Prefix>
            <PinIcon />
          </DataTableActionsMenu.Prefix>
          {intl.formatMessage(dataTableMessages.pinColumnRightLabel)}
        </DataTableActionsMenu.Item>
      ) : null}
      {pinnedState !== false ? (
        <DataTableActionsMenu.Item
          onSelect={() => {
            header.pin(false);
            onSelect?.();
          }}
          data-dt-name="ColumnActionUnpin"
        >
          <DataTableActionsMenu.Prefix>
            <PinOffIcon />
          </DataTableActionsMenu.Prefix>
          {intl.formatMessage(dataTableMessages.unpinColumnLabel)}
        </DataTableActionsMenu.Item>
      ) : null}
    </>
  );
};

/**
 * Out of the box column action for toggling the font style.
 * This component can only be used as a child element of `<TableActionsMenu>`.
 */
const ColumnFontStyleAction = ({
  onSelect,
}: DataTableActionsMenuActionProps): JSX.Element => {
  const intl = useIntl();
  const column = useDataTableColumn();

  return (
    <DataTableActionsMenu.Item
      onSelect={() => {
        column.toggleFontStyle();
        onSelect?.();
      }}
    >
      <DataTableActionsMenu.Prefix>
        <WrapTextIcon />
      </DataTableActionsMenu.Prefix>
      {column.getFontStyle() === 'text'
        ? intl.formatMessage(dataTableMessages.fontStyleCodeLabel)
        : intl.formatMessage(dataTableMessages.fontStyleTextLabel)}
    </DataTableActionsMenu.Item>
  );
};

/**
 * Slot components that can be used to configure the user actions menu.
 * @public
 */
export const DataTableActionsMenu = Object.assign(
  function DataTableActionsMenuInternal(props: PropsWithChildren) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{props.children}</>;
  },
  {
    Intent: MenuIntent,
    Item: MenuItem,
    // DEPRECATION: [APPDEV-15604] BREAKING-CHANGE: Remove TableActionsMenu.ItemIcon.
    /**
     * The DataTableActionsMenu.ItemIcon is deprecated, use DataTableActionsMenu.Prefix instead.
     * @deprecated Use `TableActionsMenu.Prefix` instead. This slot will be removed in the future.
     */
    ItemIcon: MenuPrefix,
    Prefix: MenuPrefix,
    Suffix: MenuSuffix,
    Group: MenuGroup,
    Label: MenuLabel,
    CopyItem: CopyItem,
    SubMenu: MenuSub,
    SubContent: MenuSubContent,
    SubTrigger: MenuSubTrigger,
    Link: MenuLink,
    LineWrap: LineWrapAction,
    HideColumn: HideColumnAction,
    ColumnOrder: ColumnOrderAction,
    ColumnPinning: ColumnPinningAction,
    ColumnFontStyle: ColumnFontStyleAction,
  },
);
